/**
 * PageHeaderElement — F12.H T12.H.2.
 *
 * Nagłówek strony rekordu — duży tytuł na górze (np. „Faktura sprzedaży"),
 * opcjonalnie z ikoną i podtytułem. LEAF element (bez children).
 *
 * Renderowany jako semantyczny `<header>` element nad sekcjami formularza.
 *
 * Settings: { title, subtitle?, icon?, align?: 'left'|'center' }
 */

import { useTranslation } from 'react-i18next';
import { FileText, type LucideIcon } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import type {
  LayoutElementDefinition,
  LayoutElementPropertiesEditorProps,
} from '@/components/fields/LayoutElementRegistry';
import { Input } from '@/components/Input';
import { IconPicker } from '@/components/modules/IconPicker';

type PageHeaderAlign = 'left' | 'center';

function PageHeaderProperties({ settings, onChange }: LayoutElementPropertiesEditorProps) {
  const { t } = useTranslation();
  const title = String(settings.title ?? '');
  const subtitle = String(settings.subtitle ?? '');
  const icon = String(settings.icon ?? 'FileText');
  const align = (settings.align as PageHeaderAlign) ?? 'left';
  return (
    <div className="space-y-3" data-testid="page-header-properties-editor">
      <div>
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          {t('tenant.systemModuleBuilder.pageHeader.titleLabel')}
        </label>
        <Input
          type="text"
          value={title}
          onChange={(e) => onChange({ title: e.target.value })}
          className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
          data-testid="page-header-prop-title"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          {t('tenant.systemModuleBuilder.pageHeader.subtitleLabel')}
        </label>
        <Input
          type="text"
          value={subtitle}
          onChange={(e) => onChange({ subtitle: e.target.value })}
          className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
          data-testid="page-header-prop-subtitle"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          {t('tenant.systemModuleBuilder.pageHeader.iconLabel')}
        </label>
        <IconPicker
          value={icon}
          onChange={(name) => onChange({ icon: name })}
          ariaLabel={t('tenant.systemModuleBuilder.pageHeader.iconLabel')}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          {t('tenant.systemModuleBuilder.pageHeader.alignLabel')}
        </label>
        <Dropdown
          value={align}
          onChange={(v) => onChange({ align: v as PageHeaderAlign })}
          className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
          data-testid="page-header-prop-align"
          options={[
            { value: 'left', label: t('tenant.systemModuleBuilder.pageHeader.alignLeft') },
            { value: 'center', label: t('tenant.systemModuleBuilder.pageHeader.alignCenter') },
          ]}
        />
      </div>
    </div>
  );
}

export const PageHeaderElement: LayoutElementDefinition = {
  type: 'page-header',
  category: 'struktura',
  isContainer: false,
  // View-level (T2): renderowany nad paskiem zakładek; JEDEN na widok (singleton).
  viewLevel: true,
  viewLevelSingleton: true,
  label: 'Nagłówek strony',
  icon: 'Heading1',
  defaultSettings: () => ({
    title: 'Nagłówek strony',
    subtitle: '',
    icon: 'FileText',
    align: 'left',
  }),
  propertiesEditor: (props) => <PageHeaderProperties {...props} />,
  render: ({ element, mode }) => {
    const settings = (element.settings ?? {}) as {
      title?: string;
      subtitle?: string;
      icon?: string;
      align?: 'left' | 'center';
    };
    const align = settings.align ?? 'left';
    const IconComponent: LucideIcon = FileText; // T12.H.9: dynamic Lucide lookup w panelu właściwości
    const isBuilder = mode === 'builder';

    return (
      <header
        className={`border-b border-border-primary pb-3 ${align === 'center' ? 'text-center' : ''} ${isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition' : ''}`}
        data-testid={`page-header-element-${element.id}`}
        data-element-type="page-header"
      >
        <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''}`}>
          {settings.icon && (
            <IconComponent className="h-6 w-6 text-accent-500" aria-hidden="true" />
          )}
          <h1 className="text-2xl font-bold text-text-primary">
            {settings.title || (isBuilder ? '(pusty tytuł)' : '')}
          </h1>
        </div>
        {settings.subtitle && (
          <p className="mt-1 text-sm text-text-secondary">{settings.subtitle}</p>
        )}
      </header>
    );
  },
};
